import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Spin } from 'antd';
import {
  AimOutlined,
  CheckCircleFilled,
  DeleteOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { FixDecision, FixProposal, ResumeFixRaw } from '../../types';
import {
  getCategoryLabel,
  getPriorityLabel,
  getPriorityMeta,
} from '../drawers/review/reviewDisplay';
import css from './TailorFixCard.module.scss';

/** Lifecycle of an async comment card. Review and delete cards are always
 *  'ready' (nothing to wait on). 'processing' = the worker is still drafting;
 *  'failed' = it gave up (offer a retry). */
export type FixCardStatus = 'processing' | 'ready' | 'failed';
/** 'delete' cards render as a removal (struck `before`, no `after`); 'add'
 *  cards render the new line only (no struck `before`). */
export type FixCardOp = 'replace' | 'delete' | 'add';

interface TailorFixCardProps {
  /** Raw snake_case fix exactly as stored in fixContext.fixes (the cached
   *  model_dump), NOT the camelCase review DTO — see types/index.ts. */
  fix: ResumeFixRaw;
  /** The concrete edit to apply. Null when the backstop dropped it — the card
   *  degrades to the review's plain suggestion with Skip only; acting on it
   *  routes through preview comments instead. */
  proposal: FixProposal | null;
  /** Whether the proposal targets a free-text field the user can rewrite. False
   *  for STRUCTURAL targets (an education date drop, whose display text isn't a
   *  content field) — editing there is silently discarded, so the Edit button is
   *  hidden and only Accept/Skip remain. Defaults to true. */
  editable?: boolean;
  /** Latest decision for this fix; non-null collapses the card. */
  decision: FixDecision | null;
  /** True while any card is regenerating — gates the mutating actions. */
  busy: boolean;
  /** Async lifecycle for comment cards. Defaults to 'ready' (review/delete
   *  cards, and legacy synchronous comments, are always ready). */
  status?: FixCardStatus;
  /** 'delete' renders the card as a removal. Defaults to 'replace'. */
  op?: FixCardOp;
  /** User-safe reason shown on a 'failed' card. */
  failureReason?: string | null;
  /** Hint shown under a fallback (proposal-less) card. Defaults to routing
   *  the user into preview comments — override it for cards that came FROM a
   *  preview comment, where that instruction would be circular. Pass null to
   *  render no hint at all: a card whose own suggestion already tells the user
   *  where the edit lives (the server's structural guidance) must not repeat
   *  it in a second paragraph. */
  fallbackHint?: string | null;
  onAccept: (text: string) => void;
  onSkip: () => void;
  onUndo: () => void;
  /** Re-run a failed comment (re-POST). Only wired for 'failed' cards. */
  onRetry?: () => void;
  /** Momentarily ring-flash this card and scroll it into view — used to draw
   *  the eye to a card the user just created by clicking the preview. */
  flash?: boolean;
}

const DEFAULT_FALLBACK_HINT =
  'Want to act on this? Click the matching section in the preview and tell us what to change.';

const DECISION_LABELS: Record<FixDecision['decision'], string> = {
  accepted: 'Accepted',
  edited: 'Edited',
  skipped: 'Skipped',
};

const TailorFixCard: React.FC<TailorFixCardProps> = ({
  fix,
  proposal,
  editable = true,
  decision,
  busy,
  status = 'ready',
  op = 'replace',
  failureReason,
  fallbackHint = DEFAULT_FALLBACK_HINT,
  onAccept,
  onSkip,
  onUndo,
  onRetry,
  flash = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const editBtnRef = useRef<HTMLButtonElement>(null);
  const primaryBtnRef = useRef<HTMLButtonElement>(null);
  const undoBtnRef = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);
  const hadDecision = useRef(decision !== null);

  // A freshly-created card (from a preview click) flashes and pulls itself into
  // view so the user sees where their request landed in the list. The .flash
  // class carries the ring animation; here we just center it (reduced-motion:
  // an instant jump, no smooth scroll).
  useEffect(() => {
    if (!flash) return;
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    // Optional call: jsdom (tests) doesn't implement scrollIntoView.
    rootRef.current?.scrollIntoView?.({
      block: 'center',
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }, [flash]);

  const flashClass = flash ? ` ${css.flash}` : '';

  // Return focus to the Edit button when leaving edit mode, so keyboard/screen-
  // reader users aren't dropped at the document root after committing.
  useEffect(() => {
    if (wasEditing.current && !editing) {
      editBtnRef.current?.focus();
    }
    wasEditing.current = editing;
  }, [editing]);

  // A decision collapses the card into a receipt and Undo re-expands it —
  // both replace the element that held focus. Move focus across the boundary
  // (onto the receipt's Undo when collapsing, back onto the primary action
  // when re-expanding) and discard any in-progress edit so a stale draft
  // can't resurface later.
  useEffect(() => {
    const has = decision !== null;
    if (has && !hadDecision.current) {
      setEditing(false);
      setDraft('');
      undoBtnRef.current?.focus();
    } else if (!has && hadDecision.current) {
      primaryBtnRef.current?.focus();
    }
    hadDecision.current = has;
  }, [decision]);

  const isAgeSignal = fix.category === 'age_signals';
  const isDelete = op === 'delete';
  const isAdd = op === 'add';
  const priorityClass = css[`priority_${fix.priority}`] ?? '';
  const meta = getPriorityMeta(fix.priority);
  const tagClass = css[`tag_${meta.tone}`] ?? '';

  // A processing comment: the same index card, mid-draft. The user's note
  // stands in for the issue line so they remember what they asked while the
  // worker fills before/after in place. (A decided card always collapses to its
  // receipt below, even mid-status — e.g. a failed comment the user skipped.)
  if (status === 'processing' && !decision) {
    return (
      <div
        ref={rootRef}
        className={`${css.card} ${css.pending}${flashClass}`}
        aria-busy="true"
      >
        <div className={css.header}>
          <span className={css.categoryWrap}>
            <span className={css.category}>Comment</span>
          </span>
        </div>
        {fix.issue && <p className={css.issue}>{fix.issue}</p>}
        <div className={css.pendingRow} role="status">
          <Spin size="small" />
          <span className={css.pendingText}>Drafting a stronger version…</span>
        </div>
      </div>
    );
  }

  // A failed comment: keep the note visible, say what went wrong in the
  // interface's voice, and offer the two honest next steps — retry or skip.
  if (status === 'failed' && !decision) {
    return (
      <div ref={rootRef} className={`${css.card} ${css.failed}${flashClass}`}>
        <div className={css.header}>
          <span className={css.categoryWrap}>
            <span className={css.category}>Comment</span>
          </span>
        </div>
        {fix.issue && <p className={css.issue}>{fix.issue}</p>}
        <p className={css.failNote} role="alert">
          <ExclamationCircleOutlined aria-hidden="true" />
          <span>
            {failureReason
              ? `We couldn't draft this — ${failureReason}`
              : "We couldn't draft this one."}
          </span>
        </p>
        <div className={css.actions}>
          <Button
            type="primary"
            ref={primaryBtnRef}
            disabled={busy}
            onClick={() => onRetry?.()}
          >
            Try again
          </Button>
          <Button type="text" disabled={busy} onClick={onSkip}>
            Skip
          </Button>
        </div>
      </div>
    );
  }

  if (decision) {
    const skipped = decision.decision === 'skipped';
    // A removal's receipt speaks in removal terms: "Removed" once applied,
    // "Kept" when the user declined it.
    const label = isDelete
      ? skipped
        ? 'Kept'
        : 'Removed'
      : DECISION_LABELS[decision.decision];
    return (
      // role="status" so the decision is announced when the receipt replaces
      // the expanded card.
      <div
        ref={rootRef}
        className={`${css.card} ${css.decided}${flashClass}`}
        role="status"
      >
        <div className={css.decidedRow}>
          {skipped ? (
            <MinusCircleOutlined
              className={css.decidedIconSkipped}
              aria-hidden="true"
            />
          ) : (
            <CheckCircleFilled
              className={css.decidedIconDone}
              aria-hidden="true"
            />
          )}
          <span className={css.decidedLabel}>{label}</span>
          <span className={css.decidedIssue}>{fix.issue}</span>
          <Button
            type="link"
            size="small"
            ref={undoBtnRef}
            disabled={busy}
            onClick={onUndo}
          >
            Undo
          </Button>
        </div>
      </div>
    );
  }

  // A ready removal: the card's mirror image — the content that will disappear,
  // struck through, with a decisive (destructive) Remove and a Keep-it out.
  if (isDelete && proposal) {
    return (
      <div ref={rootRef} className={`${css.card} ${css.deleteCard}${flashClass}`}>
        <div className={css.header}>
          <span className={css.categoryWrap}>
            <DeleteOutlined className={css.removeIcon} aria-hidden="true" />
            <span className={css.category}>Remove</span>
          </span>
        </div>
        <p className={css.issue}>{fix.issue}</p>
        <div className={css.diff}>
          <p className={css.before}>{proposal.before}</p>
          {proposal.rationale && (
            <p className={css.rationale}>{proposal.rationale}</p>
          )}
        </div>
        <div className={css.actions}>
          <Button
            danger
            type="primary"
            ref={primaryBtnRef}
            disabled={busy}
            onClick={() => onAccept('')}
          >
            Remove
          </Button>
          <Button type="text" disabled={busy} onClick={onSkip}>
            Keep it
          </Button>
        </div>
      </div>
    );
  }

  const startEditing = () => {
    if (!proposal) return; // Edit only exists on proposal cards.
    setDraft(proposal.after);
    setEditing(true);
  };

  const saveEdit = () => {
    setEditing(false);
    onAccept(draft.trim());
  };

  return (
    <div
      ref={rootRef}
      className={`${css.card} ${priorityClass} ${isAgeSignal ? css.ageSignal : ''}${flashClass}`}
    >
      <div className={css.header}>
        <span className={css.categoryWrap}>
          {isAgeSignal && (
            <AimOutlined
              className={css.missionIcon}
              aria-label="Age-bias signal — our core focus"
            />
          )}
          <span className={css.category}>{getCategoryLabel(fix.category)}</span>
        </span>
        <span
          className={`${css.tag} ${tagClass}`}
          aria-label={getPriorityLabel(fix.priority)}
        >
          <span className={css.tagSymbol} aria-hidden="true">
            {meta.symbol}
          </span>
          {meta.short}
        </span>
      </div>

      <p className={css.issue}>{fix.issue}</p>
      <p className={css.why}>{fix.why_it_matters}</p>

      {proposal ? (
        <div className={css.diff}>
          {/* An add has nothing to strike through — show only the new line,
              marked as an addition. */}
          {!isAdd && <p className={css.before}>{proposal.before}</p>}
          <p className={`${css.after}${isAdd ? ` ${css.afterAdd}` : ''}`}>
            {proposal.after}
          </p>
          <p className={css.rationale}>{proposal.rationale}</p>
        </div>
      ) : (
        <>
          <p className={css.suggestion}>{fix.suggestion}</p>
          {/* Suppressed (null) rather than emptied — an empty paragraph would
              still take its margin under the suggestion. */}
          {fallbackHint && <p className={css.fallbackHint}>{fallbackHint}</p>}
        </>
      )}

      {editing ? (
        <div className={css.editArea}>
          <Input.TextArea
            autoFocus
            aria-label="Edit suggested text"
            autoSize={{ minRows: 2, maxRows: 6 }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className={css.actions}>
            <Button
              type="primary"
              disabled={busy || !draft.trim()}
              onClick={saveEdit}
            >
              Save
            </Button>
            <Button onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className={css.actions}>
          {proposal ? (
            <>
              <Button
                type="primary"
                ref={primaryBtnRef}
                disabled={busy}
                onClick={() => onAccept(proposal.after)}
              >
                Accept
              </Button>
              {editable && (
                <Button ref={editBtnRef} disabled={busy} onClick={startEditing}>
                  Edit
                </Button>
              )}
              <Button type="text" disabled={busy} onClick={onSkip}>
                Skip
              </Button>
            </>
          ) : (
            // No section target for the text in fallback mode, so Skip is the
            // only decision here — real edits go through preview comments.
            <Button
              type="text"
              ref={primaryBtnRef}
              disabled={busy}
              onClick={onSkip}
            >
              Skip
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default TailorFixCard;
